// ─── NAME GUARD ──────────────────────────────────────────────────────────────
// Haley, 2026-10-06: "how do we make it so there are no hallucinations".
//
// A model cannot be told not to invent; it can be caught. The one invention a
// deterministic check can see is a NEW NAME: a relative, a servant, a room, a
// place or a person the reply introduces that appears nowhere in what the
// model was shown. The VOICE FIDELITY laws already say "never add a relative,
// a child, a servant or a room with a name that is not written"; this is the
// check behind the law. A hit regenerates the reply once with the name banned
// (the same path the repeat detector uses), and every hit is logged so the
// rate is a number the writers can watch, not a feeling.
//
// Allowed = every capitalised token in the prompt the model received (the
// system prompt, the transcript and the player's message) plus a carve-out of
// ordinary capitalised words. A roster name that is also an ordinary word
// (Will, Hope, Grace, Summer, Red, Luna, Uncle, God …) is only ever allowed
// through the prompt, never flagged on its own: the patterns below need a
// relative, a place or a speaking person, not a bare capital.
import { supabase } from './supabaseClient.js';

const CAP = /\b([A-Z][a-z]{2,})(?:'s)?\b/g;

// Ordinary words that start sentences or get capitalised in roleplay prose.
const COMMON = new Set(`
the a an and or but if then so to of in on at for with from by as is are was were be been being it its this that these those i you he she we they me him her us them
my your his our their not no yes do does did have has had can could will would should may might must just very really also too there here what which who whom when where
why how all any some more most much many such only own same than into out up down over under again further once about against between because while during before after
above below off oh hey well yeah okay ok fine sure right look listen wait please sorry thanks thank hmm hm mmm ugh ew wow whoa damn dammit shit fuck hell god jesus christ
good morning evening night afternoon today tonight tomorrow yesterday now later soon never always maybe perhaps still even ever already almost nobody somebody someone
something nothing anything everything everyone everybody anyone anybody none either neither both each every another other first second third last next new old little big
long short high low early late real true false easy hard fast slow hot cold warm cool dark light black white red blue green gold silver sweet shortstack darling princess
honey babe baby love dear sir madam miss mister mr mrs ms dr lady lord king queen prince princess alpha beta omega luna uncle aunt mom dad mama papa mother father brother
sister son daughter kid kids boy girl man woman men women people friend friends family pack house home room kitchen bedroom bathroom porch car truck phone text texts
monday tuesday wednesday thursday friday saturday sunday january february march april may june july august september october november december christmas halloween
thanksgiving easter valentine valentines newyear birthday weekend week month year
english french spanish russian german italian japanese chinese korean latin american southern northern eastern western european asian african
internet tv netflix youtube google discord instagram tiktok twitter facebook uber amazon starbucks walmart target costco coke pepsi tylenol advil benadryl ibuprofen
ps playstation xbox nintendo switch zelda mario pokemon marvel disney pixar hulu spotify apple android iphone
heaven hades zeus thor odin loki asgard atlantis olympus fate fates
ouch ow psst shh hush aww eh huh meh pfft tsk ahem umm uhh haha lol omg ooh
`.trim().split(/\s+/));

const RELATION = '(?:(?:older|younger|little|big|twin|half|step|baby|late|dead|eldest|youngest|oldest)[- ])?(?:brother|sister|mother|father|mom|dad|mama|papa|son|daughter|cousin|uncle|aunt|grandmother|grandfather|grandma|grandpa|nephew|niece|wife|husband|mate|fiancée|fiancé|beta|gamma|omega|luna|butler|maid|driver|chauffeur|guard|bodyguard|assistant|advisor|secretary|nanny|cook|chef|cat|dog|horse|pup|cub|kitten|puppy)';
const RELATIVE_BEFORE = new RegExp(`\\b(?:[Mm]y|[Hh]is|[Hh]er|[Tt]heir|[Oo]ur) ${RELATION}[,:]?\\s+(?:named |called )?([A-Z][a-z]{2,})\\b`, 'g');
const RELATIVE_AFTER = new RegExp(`\\b([A-Z][a-z]{2,}), (?:[Mm]y|[Hh]is|[Hh]er|[Tt]heir|[Oo]ur) ${RELATION}\\b`, 'g');
const PLACE_NAMED = /\b[Tt]he ([A-Z][a-z]{2,}(?: [A-Z][a-z]{2,})?) (Room|Hall|Wing|Suite|Lounge|Library|Garden|Gardens|Tower|House|Estate|Manor|Cabin|Lodge|Bar|Cafe|Café|Diner|Club|Inn|Tavern|Pub|Hotel|Motel|Theater|Theatre|Park|Beach|Cove|Harbor|Harbour|Bridge|Street|Avenue|Road|Lake|River|Falls|Mountain|Ridge|Valley|Forest|Woods|Castle|Palace|Temple|Chapel|Church|Academy|School|University|Hospital|Clinic|Station|Market|Shop|Store|Ranch|Farm|Mill|Mine|Camp|Compound|Den|Keep|Hollow|Springs|Point)\b/g;
const PLACE_POSSESSIVE = /\b(?:at|to|in|from|near|behind|inside|outside) ([A-Z][a-z]{2,})'s (?:place|house|apartment|office|bar|cafe|café|diner|club|lodge|cabin|shop|room|garage|kitchen|porch)\b/g;
const SPEAKER = /\b([A-Z][a-z]{2,})\s+(?:says?|said|asks?|asked|replie[sd]|told|tells|answer(?:s|ed)|murmur(?:s|ed)|whisper(?:s|ed)|mutter(?:s|ed)|laugh(?:s|ed)|chuckle[sd]|nod(?:s|ded)|smile[sd]|grin(?:s|ned)|call(?:s|ed)|text(?:s|ed)|walk(?:s|ed) in|arrive[sd]|show(?:s|ed) up|appear(?:s|ed)|wave[sd]|shrug(?:s|ged)|sigh(?:s|ed))\b/g;

/** Every capitalised token in a text, lower-cased. */
export function capitalisedTokens(text) {
  const out = new Set();
  const s = String(text || '');
  let m;
  CAP.lastIndex = 0;
  while ((m = CAP.exec(s))) out.add(m[1].toLowerCase());
  return out;
}

/**
 * Names the reply introduces that the model was never shown.
 * @param {string} reply
 * @param {string} shownText  everything the model received: system prompt, transcript, the player's message
 * @param {{extra?: string[]}} [opts]  extra allowed names (roster names, aliases, display names)
 * @returns {{name: string, kind: 'relative'|'place'|'speaker'|'person', context: string}[]}
 */
export function findUnknownNames(reply, shownText, opts = {}) {
  const r = String(reply || '');
  if (!r.trim()) return [];
  const allowed = capitalisedTokens(shownText);
  for (const e of (opts.extra || [])) for (const w of String(e || '').split(/\s+/)) if (w) allowed.add(w.toLowerCase());
  const known = (w) => !w || allowed.has(w.toLowerCase()) || COMMON.has(w.toLowerCase());
  const hits = new Map();
  const add = (name, kind, context) => { const k = name.toLowerCase(); if (!hits.has(k)) hits.set(k, { name, kind, context: context.replace(/\s+/g, ' ').trim().slice(0, 140) }); };
  let m;
  for (const rx of [RELATIVE_BEFORE, RELATIVE_AFTER]) {
    rx.lastIndex = 0;
    while ((m = rx.exec(r))) if (!known(m[1])) add(m[1], 'relative', r.slice(Math.max(0, m.index - 40), m.index + m[0].length + 20));
  }
  // a named place is judged as a PHRASE: "the Blue Room" is new even though
  // "blue" and "room" are ordinary words; "the Pack House" passes because the
  // phrase is in the prompt
  const shownLower = String(shownText || '').toLowerCase().replace(/\s+/g, ' ');
  PLACE_NAMED.lastIndex = 0;
  while ((m = PLACE_NAMED.exec(r))) {
    const phrase = `${m[1]} ${m[2]}`;
    if (!shownLower.includes(phrase.toLowerCase())) add(phrase, 'place', r.slice(Math.max(0, m.index - 30), m.index + m[0].length + 20));
  }
  PLACE_POSSESSIVE.lastIndex = 0;
  while ((m = PLACE_POSSESSIVE.exec(r))) if (!known(m[1])) add(m[1], 'place', r.slice(Math.max(0, m.index - 30), m.index + m[0].length + 20));
  SPEAKER.lastIndex = 0;
  while ((m = SPEAKER.exec(r))) if (!known(m[1])) add(m[1], 'speaker', r.slice(Math.max(0, m.index - 40), m.index + m[0].length + 20));
  // a bare unknown capital used more than once, at least once mid-sentence, is a person
  const counts = new Map(); const mid = new Map();
  CAP.lastIndex = 0;
  while ((m = CAP.exec(r))) {
    const w = m[1];
    if (known(w)) continue;
    counts.set(w, (counts.get(w) || 0) + 1);
    const before = r.slice(Math.max(0, m.index - 3), m.index);
    const sentenceStart = m.index === 0 || /[.!?…"“\n]\s*$/.test(before) || /^\s*$/.test(before);
    if (!sentenceStart) mid.set(w, (mid.get(w) || 0) + 1);
  }
  for (const [w, n] of counts) if (n >= 2 && (mid.get(w) || 0) >= 1 && !hits.has(w.toLowerCase())) add(w, 'person', `used ${n} times`);
  return [...hits.values()];
}

/** The one-turn direction a regeneration gets when the guard fires. */
export function guardDirective(hits, charName = 'you') {
  if (!hits || !hits.length) return '';
  const parts = hits.map(h => h.kind === 'relative' ? `"${h.name}" as a relative or member of the household`
    : h.kind === 'place' ? `a place called "${h.name}"`
    : h.kind === 'speaker' ? `a person called "${h.name}"`
    : `"${h.name}"`);
  return `The previous attempt invented ${parts.join(', ')}. No such person, place or room exists in this story: nothing in the cast, the world cast, the places or the conversation names them. Rewrite the reply, as ${charName}, using only the people and places already written above, or leave the extra unnamed ("a maid", "the back room"). Do not use ${hits.map(h => `"${h.name}"`).join(' or ')} anywhere.`;
}

/** Telemetry: one row per guard event. Fire-and-forget; never blocks a reply. */
export function logGuardEvent({ userId, characterId, surface, hits, action, retried = false }) {
  try {
    if (!userId || !characterId || !hits?.length) return;
    supabase.from('reply_guard_log').insert({
      user_id: userId, character_id: characterId, surface: String(surface || 'dm'),
      names: hits.map(h => h.name).slice(0, 12), kinds: hits.map(h => h.kind).slice(0, 12),
      action: String(action || 'logged'), retried: !!retried,
    }).then(null, () => {});
  } catch { /* telemetry is best-effort */ }
}
